/** Cubic-bezier path math, matching Scratch's Paper.js reshape tool. */

export const KAPPA = 0.5522847498307936;

export function seg(x, y, ix = 0, iy = 0, ox = 0, oy = 0, smooth = false) {
  return { x, y, ix, iy, ox, oy, smooth: !!smooth };
}

export function cloneSegs(segs) {
  return (segs || []).map((s) => ({ ...s }));
}

export function ptsToSegs(pts) {
  return (pts || []).map((p) => seg(p.x, p.y));
}

export function hasHandles(s) {
  return s && (s.ix || s.iy || s.ox || s.oy);
}

export function ensurePath(shape) {
  if (!shape) return shape;
  if (shape.type === 'path') {
    if (!shape.segs) shape.segs = ptsToSegs(shape.pts || []);
    delete shape.pts;
    return shape;
  }
  if (shape.type === 'ellipse') return ellipseToPath(shape);
  if (shape.type === 'rect') return rectToPath(shape);
  if (shape.type === 'line') return lineToPath(shape);
  return shape;
}

export function ellipseToPath(s) {
  const rx = Math.abs(s.w / 2) || 1;
  const ry = Math.abs(s.h / 2) || 1;
  const cx = s.x, cy = s.y;
  const kx = rx * KAPPA, ky = ry * KAPPA;
  s.type = 'path';
  s.closed = true;
  s.segs = [
    seg(cx + rx, cy, 0, -ky, 0, ky, true),
    seg(cx, cy + ry, kx, 0, -kx, 0, true),
    seg(cx - rx, cy, 0, ky, 0, -ky, true),
    seg(cx, cy - ry, -kx, 0, kx, 0, true),
  ];
  delete s.w;
  delete s.h;
  delete s.circle;
  return s;
}

export function rectToPath(s) {
  s.type = 'path';
  s.closed = true;
  s.segs = [
    seg(s.x, s.y),
    seg(s.x + s.w, s.y),
    seg(s.x + s.w, s.y + s.h),
    seg(s.x, s.y + s.h),
  ];
  delete s.w;
  delete s.h;
  return s;
}

export function lineToPath(s) {
  s.type = 'path';
  s.closed = false;
  s.segs = [seg(s.x, s.y), seg(s.x2, s.y2)];
  delete s.x2;
  delete s.y2;
  return s;
}

export function drawPath(g, segs, closed) {
  if (!segs?.length) return;
  g.beginPath();
  g.moveTo(segs[0].x, segs[0].y);
  const n = segs.length;
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) {
    const a = segs[i];
    const b = segs[(i + 1) % n];
    if (!a.ox && !a.oy && !b.ix && !b.iy) g.lineTo(b.x, b.y);
    else g.bezierCurveTo(a.x + a.ox, a.y + a.oy, b.x + b.ix, b.y + b.iy, b.x, b.y);
  }
  if (closed) g.closePath();
}

export function pathToSvgD(segs, closed) {
  if (!segs?.length) return '';
  const f = (n) => (Math.round(n * 100) / 100);
  let d = `M${f(segs[0].x)} ${f(segs[0].y)}`;
  const n = segs.length;
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) {
    const a = segs[i];
    const b = segs[(i + 1) % n];
    if (!a.ox && !a.oy && !b.ix && !b.iy) d += `L${f(b.x)} ${f(b.y)}`;
    else {
      d += `C${f(a.x + a.ox)} ${f(a.y + a.oy)} ${f(b.x + b.ix)} ${f(b.y + b.iy)} ${f(b.x)} ${f(b.y)}`;
    }
  }
  if (closed) d += 'Z';
  return d;
}

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export function cubicPoint(p0, p1, p2, p3, t) {
  const a = lerp(p0, p1, t);
  const b = lerp(p1, p2, t);
  const c = lerp(p2, p3, t);
  return lerp(lerp(a, b, t), lerp(b, c, t), t);
}

export function splitCubic(p0, p1, p2, p3, t) {
  const p01 = lerp(p0, p1, t);
  const p12 = lerp(p1, p2, t);
  const p23 = lerp(p2, p3, t);
  const p012 = lerp(p01, p12, t);
  const p123 = lerp(p12, p23, t);
  const p0123 = lerp(p012, p123, t);
  return { mid: p0123, left1: p01, left2: p012, right1: p123, right2: p23 };
}

export function curveOf(segs, i, closed) {
  const n = segs.length;
  const a = segs[i];
  const b = segs[(i + 1) % n];
  if (!closed && i >= n - 1) return null;
  return {
    p0: { x: a.x, y: a.y },
    p1: { x: a.x + (a.ox || 0), y: a.y + (a.oy || 0) },
    p2: { x: b.x + (b.ix || 0), y: b.y + (b.iy || 0) },
    p3: { x: b.x, y: b.y },
    a, b,
  };
}

export function hitAnchor(p, segs, tol) {
  let best = -1, bestD = tol;
  for (let i = 0; i < segs.length; i++) {
    const d = Math.hypot(p.x - segs[i].x, p.y - segs[i].y);
    if (d <= bestD) { bestD = d; best = i; }
  }
  return best;
}

export function hitHandle(p, segs, active, tol) {
  if (active == null || !segs[active]) return null;
  const s = segs[active];
  if ((s.ix || s.iy) && Math.hypot(p.x - (s.x + s.ix), p.y - (s.y + s.iy)) <= tol) {
    return { kind: 'hin', i: active };
  }
  if ((s.ox || s.oy) && Math.hypot(p.x - (s.x + s.ox), p.y - (s.y + s.oy)) <= tol) {
    return { kind: 'hout', i: active };
  }
  return null;
}

export function nearestOnPath(p, segs, closed) {
  const n = segs.length;
  if (n < 2) return null;
  const count = closed ? n : n - 1;
  let best = null, bestD = Infinity;
  for (let i = 0; i < count; i++) {
    const c = curveOf(segs, i, closed);
    if (!c) continue;
    for (let s = 0; s <= 24; s++) {
      const t = s / 24;
      const q = cubicPoint(c.p0, c.p1, c.p2, c.p3, t);
      const d = Math.hypot(p.x - q.x, p.y - q.y);
      if (d < bestD) {
        bestD = d;
        best = { i, t, x: q.x, y: q.y, dist: d };
      }
    }
  }
  return best;
}

export function pathContains(p, segs, closed) {
  if (!closed || segs.length < 3) return false;
  const samples = [];
  const n = segs.length;
  for (let i = 0; i < n; i++) {
    const c = curveOf(segs, i, true);
    if (!c) continue;
    for (let s = 0; s < 10; s++) samples.push(cubicPoint(c.p0, c.p1, c.p2, c.p3, s / 10));
  }
  let inside = false;
  for (let i = 0, j = samples.length - 1; i < samples.length; j = i++) {
    const a = samples[i], b = samples[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / ((b.y - a.y) || 1) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

export function insertAt(segs, closed, hit) {
  const c = curveOf(segs, hit.i, closed);
  if (!c) return hit.i + 1;
  const t = Math.min(0.92, Math.max(0.08, hit.t));
  const sp = splitCubic(c.p0, c.p1, c.p2, c.p3, t);
  const a = segs[hit.i];
  const b = segs[(hit.i + 1) % segs.length];
  a.ox = sp.left1.x - a.x;
  a.oy = sp.left1.y - a.y;
  b.ix = sp.right2.x - b.x;
  b.iy = sp.right2.y - b.y;
  const mid = seg(
    sp.mid.x, sp.mid.y,
    sp.left2.x - sp.mid.x, sp.left2.y - sp.mid.y,
    sp.right1.x - sp.mid.x, sp.right1.y - sp.mid.y,
    true,
  );
  segs.splice(hit.i + 1, 0, mid);
  return hit.i + 1;
}

export function makeSmooth(segs, i) {
  const n = segs.length;
  const s = segs[i];
  const prev = segs[(i - 1 + n) % n];
  const next = segs[(i + 1) % n];
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const len = Math.hypot(dx, dy) || 1;
  const dPrev = Math.hypot(s.x - prev.x, s.y - prev.y);
  const dNext = Math.hypot(next.x - s.x, next.y - s.y);
  const tIn = (dPrev / 3) || 12;
  const tOut = (dNext / 3) || 12;
  s.ix = (-dx / len) * tIn;
  s.iy = (-dy / len) * tIn;
  s.ox = (dx / len) * tOut;
  s.oy = (dy / len) * tOut;
  s.smooth = true;
}

export function makeCorner(s) {
  s.ix = 0;
  s.iy = 0;
  s.ox = 0;
  s.oy = 0;
  s.smooth = false;
}

export function isSmooth(s) {
  if (s.smooth) return true;
  if (!hasHandles(s)) return false;
  const cross = s.ix * s.oy - s.iy * s.ox;
  const dot = s.ix * s.ox + s.iy * s.oy;
  return Math.abs(cross) < 0.8 && dot < 0;
}

export function setHandle(s, which, hx, hy, keepSmooth) {
  if (which === 'hin') {
    s.ix = hx - s.x;
    s.iy = hy - s.y;
    if (keepSmooth && isSmooth(s)) {
      const lenOut = Math.hypot(s.ox, s.oy) || Math.hypot(s.ix, s.iy);
      const lenIn = Math.hypot(s.ix, s.iy) || 1;
      s.ox = (-s.ix / lenIn) * lenOut;
      s.oy = (-s.iy / lenIn) * lenOut;
      s.smooth = true;
    } else if (!keepSmooth) {
      s.smooth = false;
    }
  } else {
    s.ox = hx - s.x;
    s.oy = hy - s.y;
    if (keepSmooth && isSmooth(s)) {
      const lenIn = Math.hypot(s.ix, s.iy) || Math.hypot(s.ox, s.oy);
      const lenOut = Math.hypot(s.ox, s.oy) || 1;
      s.ix = (-s.ox / lenOut) * lenIn;
      s.iy = (-s.oy / lenOut) * lenIn;
      s.smooth = true;
    } else if (!keepSmooth) {
      s.smooth = false;
    }
  }
}

export function translateSegs(segs, dx, dy) {
  for (const s of segs) {
    s.x += dx;
    s.y += dy;
  }
}

export function mapSegs(segs, fn) {
  for (const s of segs) {
    const a = fn(s.x, s.y);
    const hin = fn(s.x + s.ix, s.y + s.iy);
    const hout = fn(s.x + s.ox, s.y + s.oy);
    s.x = a.x;
    s.y = a.y;
    s.ix = hin.x - a.x;
    s.iy = hin.y - a.y;
    s.ox = hout.x - a.x;
    s.oy = hout.y - a.y;
  }
}

export function boundsOfSegs(segs) {
  if (!segs?.length) return { x: 0, y: 0, w: 1, h: 1 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const add = (x, y) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  };
  for (const s of segs) {
    add(s.x, s.y);
    add(s.x + s.ix, s.y + s.iy);
    add(s.x + s.ox, s.y + s.oy);
  }
  return { x: minX, y: minY, w: maxX - minX || 1, h: maxY - minY || 1 };
}

export function parsePathD(d) {
  const segs = [];
  if (!d) return { segs, closed: false };
  const tokens = d.replace(/,/g, ' ').match(/[MmLlHhVvCcSsQqTtAaZz]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || [];
  let i = 0;
  let cx = 0, cy = 0, sx = 0, sy = 0;
  let prevCmd = '';
  let lastC = null;
  let lastQ = null;
  let closed = false;

  const take = (n) => {
    const out = [];
    for (let k = 0; k < n && i < tokens.length && !/^[A-Za-z]$/.test(tokens[i]); k++) out.push(Number(tokens[i++]));
    return out;
  };
  const push = (x, y, ix = 0, iy = 0) => {
    if (segs.length) {
      const prev = segs[segs.length - 1];
      if (!prev.ox && !prev.oy && lastC) {
        /* already set */
      }
    }
    const s = seg(x, y, ix, iy, 0, 0, !!(ix || iy));
    segs.push(s);
    cx = x;
    cy = y;
    return s;
  };

  const setPrevOut = (x, y) => {
    if (!segs.length) return;
    const prev = segs[segs.length - 1];
    prev.ox = x - prev.x;
    prev.oy = y - prev.y;
  };

  while (i < tokens.length) {
    let cmd = tokens[i];
    if (/^[A-Za-z]$/.test(cmd)) i++;
    else cmd = prevCmd;
    if (!cmd) break;
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    prevCmd = cmd;

    if (C === 'Z') {
      closed = true;
      cx = sx;
      cy = sy;
      lastC = lastQ = null;
      continue;
    }
    if (C === 'M') {
      const [x, y] = take(2);
      const nx = rel ? cx + x : x, ny = rel ? cy + y : y;
      push(nx, ny);
      sx = nx; sy = ny;
      lastC = lastQ = null;
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [a, b] = take(2);
        const px = rel ? cx + a : a, py = rel ? cy + b : b;
        push(px, py);
      }
      continue;
    }
    if (C === 'L') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [x, y] = take(2);
        push(rel ? cx + x : x, rel ? cy + y : y);
        lastC = lastQ = null;
      }
      continue;
    }
    if (C === 'H') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const x = Number(tokens[i++]);
        push(rel ? cx + x : x, cy);
        lastC = lastQ = null;
      }
      continue;
    }
    if (C === 'V') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const y = Number(tokens[i++]);
        push(cx, rel ? cy + y : y);
        lastC = lastQ = null;
      }
      continue;
    }
    if (C === 'C') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [x1, y1, x2, y2, x, y] = take(6);
        const X1 = rel ? cx + x1 : x1, Y1 = rel ? cy + y1 : y1;
        const X2 = rel ? cx + x2 : x2, Y2 = rel ? cy + y2 : y2;
        const X = rel ? cx + x : x, Y = rel ? cy + y : y;
        setPrevOut(X1, Y1);
        push(X, Y, X2 - X, Y2 - Y);
        lastC = { x: X2, y: Y2 };
        lastQ = null;
      }
      continue;
    }
    if (C === 'S') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [x2, y2, x, y] = take(4);
        const X2 = rel ? cx + x2 : x2, Y2 = rel ? cy + y2 : y2;
        const X = rel ? cx + x : x, Y = rel ? cy + y : y;
        const rx = lastC ? 2 * cx - lastC.x : cx;
        const ry = lastC ? 2 * cy - lastC.y : cy;
        setPrevOut(rx, ry);
        push(X, Y, X2 - X, Y2 - Y);
        lastC = { x: X2, y: Y2 };
        lastQ = null;
      }
      continue;
    }
    if (C === 'Q') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [qx, qy, x, y] = take(4);
        const QX = rel ? cx + qx : qx, QY = rel ? cy + qy : qy;
        const X = rel ? cx + x : x, Y = rel ? cy + y : y;
        setPrevOut(cx + (2 / 3) * (QX - cx), cy + (2 / 3) * (QY - cy));
        push(X, Y, X + (2 / 3) * (QX - X), Y + (2 / 3) * (QY - Y));
        const last = segs[segs.length - 1];
        last.ix = (2 / 3) * (QX - X);
        last.iy = (2 / 3) * (QY - Y);
        lastC = null;
        lastQ = { x: QX, y: QY };
      }
      continue;
    }
    if (C === 'T') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [x, y] = take(2);
        const X = rel ? cx + x : x, Y = rel ? cy + y : y;
        const QX = lastQ ? 2 * cx - lastQ.x : cx;
        const QY = lastQ ? 2 * cy - lastQ.y : cy;
        setPrevOut(cx + (2 / 3) * (QX - cx), cy + (2 / 3) * (QY - cy));
        push(X, Y);
        const last = segs[segs.length - 1];
        last.ix = (2 / 3) * (QX - X);
        last.iy = (2 / 3) * (QY - Y);
        lastC = null;
        lastQ = { x: QX, y: QY };
      }
      continue;
    }
    if (C === 'A') {
      while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
        const [rx, ry, rot, large, sweep, x, y] = take(7);
        const X = rel ? cx + x : x, Y = rel ? cy + y : y;
        arcToCubics(cx, cy, rx, ry, rot, large, sweep, X, Y).forEach((c) => {
          setPrevOut(c[1].x, c[1].y);
          push(c[3].x, c[3].y, c[2].x - c[3].x, c[2].y - c[3].y);
        });
        lastC = lastQ = null;
      }
      continue;
    }
    break;
  }
  return { segs, closed };
}

function arcToCubics(x1, y1, rx, ry, phiDeg, large, sweep, x2, y2) {
  if (!rx || !ry || (x1 === x2 && y1 === y2)) return [[{ x: x1, y: y1 }, { x: x1, y: y1 }, { x: x2, y: y2 }, { x: x2, y: y2 }]];
  const phi = (phiDeg || 0) * Math.PI / 180;
  const cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy, y1p = -sin * dx + cos * dy;
  let rxs = Math.abs(rx), rys = Math.abs(ry);
  const lam = (x1p * x1p) / (rxs * rxs) + (y1p * y1p) / (rys * rys);
  if (lam > 1) { const s = Math.sqrt(lam); rxs *= s; rys *= s; }
  const sq = Math.max(0, (rxs * rxs * rys * rys - rxs * rxs * y1p * y1p - rys * rys * x1p * x1p)
    / (rxs * rxs * y1p * y1p + rys * rys * x1p * x1p));
  const coef = (large !== sweep ? 1 : -1) * Math.sqrt(sq);
  const cxp = coef * (rxs * y1p) / rys;
  const cyp = coef * (-rys * x1p) / rxs;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2;
  const cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const v1 = { x: (x1p - cxp) / rxs, y: (y1p - cyp) / rys };
  const v2 = { x: (-x1p - cxp) / rxs, y: (-y1p - cyp) / rys };
  const ang = (u, v) => {
    const s = u.x * v.y - u.y * v.x;
    return Math.atan2(s, u.x * v.x + u.y * v.y);
  };
  let t1 = ang({ x: 1, y: 0 }, v1);
  let dt = ang(v1, v2);
  if (!sweep && dt > 0) dt -= Math.PI * 2;
  if (sweep && dt < 0) dt += Math.PI * 2;
  const n = Math.max(1, Math.ceil(Math.abs(dt) / (Math.PI / 2)));
  const out = [];
  for (let i = 0; i < n; i++) {
    const a1 = t1 + dt * i / n;
    const a2 = t1 + dt * (i + 1) / n;
    const da = a2 - a1;
    const k = 4 / 3 * Math.tan(da / 4);
    const e = (a) => ({
      x: cx + rxs * (cos * Math.cos(a) - sin * Math.sin(a)),
      y: cy + rys * (sin * Math.cos(a) + cos * Math.sin(a)),
    });
    const p0 = e(a1), p3 = e(a2);
    const tng = (a, s) => ({
      x: s * rxs * (-cos * Math.sin(a) - sin * Math.cos(a)),
      y: s * rys * (-sin * Math.sin(a) + cos * Math.cos(a)),
    });
    const d1 = tng(a1, 1), d2 = tng(a2, 1);
    out.push([p0, { x: p0.x + k * d1.x, y: p0.y + k * d1.y }, { x: p3.x - k * d2.x, y: p3.y - k * d2.y }, p3]);
  }
  return out;
}
